"use client";

import { Button } from "@/components/ui/button";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useState } from "react";

type StripePaymentFormProps = {
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: string) => void;
  isSubmitting?: boolean;
};

export function StripePaymentForm({ onSuccess, onError, isSubmitting = false }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      // Confirm the setup intent
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Failed to process payment");
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        onSuccess(
          typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : setupIntent.payment_method.id,
        );
      }
    } catch (err) {
      onError("An unexpected error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
        <PaymentElement
          options={{
            layout: "tabs",
          }}
        />
      </div>

      <Button
        type="submit"
        disabled={!stripe || isProcessing || isSubmitting}
        className="w-full bg-[#43BEA9] hover:bg-[#3AAD98] text-white font-semibold py-3"
      >
        {isProcessing || isSubmitting ? "Processing..." : "Complete Setup & Start Trial"}
      </Button>

      <p className="text-xs text-gray-400 text-center">
        Your card will not be charged today. The 14-day trial starts immediately and you can cancel anytime before
        billing begins.
      </p>
    </form>
  );
}
